import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ArrowRight, Lock, Shield, Zap, Briefcase, TrendingUp, Target, Clock, Wrench, BarChart3, Plus } from "lucide-react";
import StickyCTABar from "@/components/StickyCTABar";
import CountdownTimer from "@/components/CountdownTimer";
import ExitIntentModal from "@/components/ExitIntentModal";
import { captureUTMParams } from "@/utils/utmUtils";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DataAnalystsPage() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [offerExpired, setOfferExpired] = useState(false);

  // Capture UTM params on mount
  useEffect(() => { captureUTMParams(); }, []);

  const startAnalysis = () => navigate("/intake");

  // FAQ state
  const [faqOpen, setFaqOpen] = useState(0);

  const stats = [
    { value: "9,200+", label: "Resumes Analyzed" },
    { value: "3.1x", label: "More Callbacks" },
    { value: "91%", label: "ATS Pass Rate After Fix" },
    { value: "₹11.4L", label: "Avg. Placed Salary" },
  ];

  const companies = [
    { name: "Google", logo: "/logos/da_google.png" },
    { name: "Flipkart", logo: "/logos/da_flipkart.png" },
    { name: "Razorpay", logo: "/logos/da_razorpay.png" },
    { name: "Amazon", logo: "/logos/da_amazon.png" },
    { name: "Swiggy", logo: "/logos/da_swiggy.png" },
    { name: "PhonePe", logo: "/logos/da_phonepe.png" },
    { name: "Zerodha", logo: "/logos/da_zerodha.png" },
    { name: "CRED", logo: "/logos/da_cred.png" },
  ];

  const features = [
    {
      icon: <Clock className="w-5 h-5" />,
      tag: "Data Analyst Critical",
      title: "ATS Readiness Analyzer",
      desc: "That skill-bar chart on your resume? ATS reads it as blank space. We check format, keyword alignment, section structure, and parser readability — then score you out of 100.",
      example: "Scans for \"SQL\", \"Python\", \"Power BI\", \"ETL\" + 180 data analyst keywords"
    },
    {
      icon: <Wrench className="w-5 h-5" />,
      title: "ATS Blocker Fix Engine",
      desc: "Don't just see the problems — fix them. Get exact, copy-paste-ready rewrites for every blocker. We turn vague bullet points into ATS-optimized, impact-driven statements.",
      example: "Before: \"Created dashboards for the team\" → After: \"Built Tableau dashboard tracking 12 KPIs, reducing report time by 6 hrs/week\""
    },
    {
      icon: <TrendingUp className="w-5 h-5" />,
      tag: "Data Analyst Critical",
      title: "Career Velocity Simulator",
      desc: "Should you stay at TCS or push for Flipkart? Learn Power BI or double down on Python? See how each decision changes your salary trajectory over 12, 24, and 36 months.",
      example: "Simulates: TCS Analyst (₹4.5L) → Razorpay Sr. Analyst (₹14L) in 12 months"
    },
    {
      icon: <Briefcase className="w-5 h-5" />,
      title: "Smart Job Matching",
      desc: "Stop scrolling 500 Naukri listings. We score every job against your exact skill set — SQL proficiency, tool stack, industry experience — and show only roles where you'll get responses.",
      example: "\"94% match: Business Analyst at PhonePe — your SQL + Python + fintech exp is a direct fit\""
    },
    {
      icon: <BarChart3 className="w-5 h-5" />,
      tag: "Data Analyst Critical",
      title: "Market Signals & Trend Insights",
      desc: "Know which analyst roles are growing (Product Analytics, AI/ML Analytics) and which are flattening (MIS Reporting) — before you invest months learning the wrong tool.",
      example: "\"Product Analyst roles up 38% YoY in Bangalore\" — Power BI + SQL most requested"
    },
    {
      icon: <Target className="w-5 h-5" />,
      title: "Anti-Random Apply Strategy",
      desc: "You're a data person — you know spray-and-pray doesn't work. We build you a targeted plan: 15–20 high-fit roles, sequenced outreach, and a 30/60/90-day career roadmap.",
      example: "Apply to 18 matched roles instead of 200 random ones — higher response rate"
    },
  ];

  const testimonials = [
    {
      initials: "SJ",
      name: "Siddharth Jain",
      img: "/avatars/siddharth_jain.jpg",
      role: "TCS MIS Analyst → Flipkart Sr. Data Analyst, Bangalore",
      rating: 5,
      text: "I had Python, SQL, Tableau — the full stack. Applied to 200+ jobs in 4 months. Zero calls. PlacementOS found 11 ATS blockers including a skills chart that was invisible to parsers. Fixed everything in one evening. Got 5 calls that week.",
      result: "Placed at Flipkart — ₹16 LPA"
    },
    {
      initials: "PR",
      name: "Pooja Reddy",
      img: "/avatars/pooja_reddy.jpg",
      role: "Wipro BI Analyst → Razorpay Product Analyst, Hyderabad",
      rating: 5,
      text: "The career velocity simulator changed everything. I was about to accept another ₹5L service company offer. PlacementOS showed me the product company path — which skills to add, which roles to target. That data gave me the confidence to aim higher.",
      result: "Moved from ₹5L → ₹13L in 5 months"
    },
    {
      initials: "NM",
      name: "Nikhil Mehta",
      img: "/avatars/nikhil_mehta.jpg",
      role: "Fresher → Jr. Data Analyst, D2C startup, Pune",
      rating: 5,
      text: "As a fresher with only a Google certificate and Kaggle projects, I was getting zero responses. PlacementOS restructured my resume to lead with project impact instead of certifications. Completely different response rate.",
      result: "3 offers in 3 weeks — accepted ₹7.5 LPA"
    },
  ];

  const faqs = [
    {
      question: "What exactly do I get for ₹99?",
      answer: "A complete career analysis: your ATS readiness score out of 100, every blocker in your resume with exact fix recommendations, a 30/60/90-day career roadmap tailored to data analyst roles, smart job matches with fit scores, and a career velocity simulation. The free analysis shows your score and top blockers — you pay only for the full detailed report."
    },
    {
      question: "Is this built specifically for data analysts?",
      answer: "Yes. Our keyword database covers 180+ data analyst-specific terms — SQL, Python, Pandas, ETL, Power BI, Tableau, statistical analysis, and more. Our career roadmaps understand the difference between a service company MIS role and a product company analytics role."
    },
    {
      question: "How is this different from Naukri or LinkedIn?",
      answer: "Naukri and LinkedIn show you jobs. PlacementOS tells you why you're not getting callbacks — and fixes it. We diagnose ATS compatibility issues, rewrite your bullet points for impact, then match you to roles where your SQL + Python + domain experience has the highest callback probability."
    },
    {
      question: "I'm a fresher with just certifications and Kaggle projects. Will this help?",
      answer: "Especially. Freshers have the most ATS issues — wrong formatting, certifications without context, projects buried at the bottom. We restructure your resume to lead with project impact instead of certificate names. The career roadmap gives you a clear first-year skill and role plan."
    },
    {
      question: "I'm at TCS / Wipro / Infosys. Can this help me switch to a product company?",
      answer: "This is one of our most common transitions. The career velocity simulator shows the exact skills gap between your service company role and target product companies (Flipkart, Razorpay, PhonePe). The typical salary jump is 2–3x."
    },
    {
      question: "Is my data safe?",
      answer: "Your resume and analysis data are encrypted end-to-end, never shared with third parties, and you can request deletion at any time. Payments are processed through secure, PCI DSS Level 1 compliant infrastructure."
    },
  ];

  return (
    <div className="ap-root min-h-screen bg-white" data-testid="data-analysts-page">
      {/* Exit Intent Modal */}
      <ExitIntentModal lpSlug="data-analysts" />
      {/* Hero Section */}
      <section id="hero" ref={heroRef} className="ap-mesh ap-grid-overlay relative pt-24 pb-10" style={{ overflowX: "clip" }}>
        <div className="ap-section max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="ap-chip ap-rise ap-rise-1" style={{ color: "var(--ap-violet)", borderColor: "var(--ap-violet-soft)", background: "var(--ap-violet-soft)" }}>
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "var(--ap-violet)" }}></span>
                Built for Data Analysts in India
              </span>
              <h1 className="ap-display ap-rise ap-rise-2 mt-5 font-extrabold text-4xl md:text-5xl mb-6 leading-tight text-[var(--ap-ink)]">
                Your SQL is solid.<br />
                Your resume gets <em className="ap-grad-text not-italic">filtered out.</em>
              </h1>
              <p className="ap-rise ap-rise-3 text-xl text-[var(--ap-muted)] mb-8 leading-relaxed">
                You can query a million rows in seconds. But your resume can't get past an ATS filter. PlacementOS finds the exact blockers killing your applications — so recruiters actually see your profile.
              </p>
              <div className="ap-rise ap-rise-4 mt-6">
                <button onClick={startAnalysis} className="ap-btn ap-btn-fire ap-btn-lg" data-testid="data-analysts-hero-cta">
                  Start My Free Analysis
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="relative ap-rise ap-rise-3">
              {/* floating 3D stat chips */}
              <div className="hidden md:flex ap-glass ap-float absolute -left-6 -top-5 z-30 items-center gap-2 px-3.5 py-2.5 rounded-2xl">
                <TrendingUp className="w-5 h-5 text-[var(--ap-green)]" />
                <div><div className="ap-display font-extrabold text-[var(--ap-ink)] leading-none">91%</div><div className="text-[11px] text-[var(--ap-muted)]">ATS pass rate</div></div>
              </div>
              <div className="hidden md:flex ap-glass ap-float-2 absolute -right-5 -bottom-6 z-30 items-center gap-2 px-3.5 py-2.5 rounded-2xl">
                <span className="w-8 h-8 rounded-full grid place-items-center text-white text-xs font-bold shrink-0" style={{ background: "var(--ap-fire)" }}>94</span>
                <div><div className="text-[12px] font-bold text-[var(--ap-ink)] leading-none">94% match</div><div className="text-[11px] text-[var(--ap-muted)]">Business Analyst · PhonePe</div></div>
              </div>
              <div className="ap-card ap-tilt overflow-hidden relative z-10 rounded-[26px]">
                <div className="bg-[var(--ap-canvas)] px-4 py-3 flex items-center gap-2 border-b border-[var(--ap-line)]">
                  <div className="flex gap-1.5">
                    <span className="w-3 h-3 bg-red-400 rounded-full"></span>
                    <span className="w-3 h-3 bg-yellow-400 rounded-full"></span>
                    <span className="w-3 h-3 bg-green-400 rounded-full"></span>
                  </div>
                  <span className="text-xs text-[var(--ap-muted)] ml-2">placementos.ai/ats-analysis</span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-20 h-20 rounded-full border-4 border-red-500 flex items-center justify-center">
                      <span className="text-2xl font-bold text-red-500">24</span>
                    </div>
                    <div>
                      <h4 className="ap-display font-semibold text-[var(--ap-ink)]">ATS Readiness Score</h4>
                      <p className="text-sm text-red-500">High rejection risk — 4 critical blockers found</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-red-500 mt-0.5">✕</span>
                      <span className="text-[var(--ap-ink-2)]">Missing "Python", "SQL", "Tableau" in skills section</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-red-500 mt-0.5">✕</span>
                      <span className="text-[var(--ap-ink-2)]">Skills chart uses image — ATS reads 0 keywords</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-amber-500 mt-0.5">!</span>
                      <span className="text-[var(--ap-ink-2)]">No quantified impact — "created dashboards" too vague</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-[var(--ap-green)] mt-0.5">✓</span>
                      <span className="text-[var(--ap-ink-2)]">Fix available — estimated new score: 89</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[var(--ap-canvas)] py-12">
        <div className="ap-section max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="ap-display text-3xl md:text-4xl font-bold text-[var(--ap-ink)] mb-2">{stat.value}</div>
                <div className="text-sm text-[var(--ap-muted)]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Companies Section */}
      <section className="bg-[var(--ap-canvas-2)] py-12">
        <div className="ap-section max-w-6xl text-center">
          <p className="text-[var(--ap-muted)] mb-8 font-medium">Our users have landed roles at</p>
          <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-6">
            {companies.map((c, idx) => (
              <div key={idx} className="h-10 md:h-12 flex items-center justify-center" title={c.name}>
                <img src={c.logo} alt={c.name} loading="lazy" className="h-full w-auto object-contain max-w-[140px]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="ap-mesh ap-grid-overlay relative py-10">
        <div className="ap-section max-w-4xl text-center">
          <p className="ap-eyebrow mb-4">The Real Problem</p>
          <h2 className="ap-h2 ap-display mb-6 text-[var(--ap-ink)]">
            You're not under-skilled.<br />
            You're <span className="ap-grad-text">invisible.</span>
          </h2>
          <p className="text-lg text-[var(--ap-ink-2)] leading-relaxed mb-12">
            You know SQL, Python, and Tableau. You've built dashboards, cleaned messy datasets, and presented insights to stakeholders. But none of that matters when ATS software filters you out before any recruiter opens your resume.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="ap-card p-6">
              <div className="ap-display text-4xl font-bold text-[var(--ap-violet)] mb-2">88%</div>
              <p className="text-[var(--ap-ink-2)]">of resumes get auto-rejected by ATS before a human ever sees them</p>
            </div>
            <div className="ap-card p-6">
              <div className="ap-display text-4xl font-bold text-[var(--ap-violet)] mb-2">200+</div>
              <p className="text-[var(--ap-ink-2)]">applicants per data analyst posting — most filtered out by formatting</p>
            </div>
            <div className="ap-card p-6">
              <div className="ap-display text-4xl font-bold text-[var(--ap-violet)] mb-2">4.5 mo</div>
              <p className="text-[var(--ap-ink-2)]">average time an Indian data analyst spends job hunting without strategy</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-10">
        <div className="ap-section max-w-4xl text-center">
          <p className="ap-eyebrow mb-4">The PlacementOS Way</p>
          <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-6">
            Fix your resume. Then apply<br />
            where you'll <span className="ap-grad-text">actually win.</span>
          </h2>
          <p className="text-lg text-[var(--ap-ink-2)] leading-relaxed">
            PlacementOS scans your resume the way ATS does, finds the exact blockers getting you rejected, and shows you which data analyst roles match your actual skill set — so every application counts.
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="bg-[var(--ap-canvas)] py-10">
        <div className="ap-section max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4" style={{ background: "var(--ap-fire)" }}>1</div>
              <h3 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-3">Upload your resume</h3>
              <p className="text-[var(--ap-muted)]">Drop your PDF or DOCX. Our engine parses it the same way Workday, Greenhouse, and Naukri's ATS do.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4" style={{ background: "var(--ap-fire)" }}>2</div>
              <h3 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-3">Get your ATS diagnosis</h3>
              <p className="text-[var(--ap-muted)]">See your score, every blocker killing your applications, and prioritized fixes — tuned for data analyst roles.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4" style={{ background: "var(--ap-fire)" }}>3</div>
              <h3 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-3">Fix, match & apply smart</h3>
              <p className="text-[var(--ap-muted)]">Apply the fixes, unlock jobs scored to your profile, and get a career roadmap from where you are to where you want to be.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-10 bg-[var(--ap-canvas)]" id="features">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">What You Get</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-4">
              Every tool a data analyst<br />needs to <span className="ap-grad-text">get placed.</span>
            </h2>
            <p className="text-lg text-[var(--ap-ink-2)]">Not a generic resume checker. Every feature is built around how data analyst hiring works in India.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="ap-card ap-pop p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--ap-violet-soft)" }}>
                    <span className="text-[var(--ap-violet)]">{feature.icon}</span>
                  </div>
                  {feature.tag && (
                    <span className="ap-chip" style={{ color: "var(--ap-violet)", borderColor: "var(--ap-violet-soft)", background: "var(--ap-violet-soft)" }}>{feature.tag}</span>
                  )}
                </div>
                <h3 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-3">{feature.title}</h3>
                <p className="text-[var(--ap-muted)] mb-4">{feature.desc}</p>
                <div className="rounded-lg border-l-4 border-[var(--ap-violet)] p-3 text-sm text-[var(--ap-ink-2)] italic" style={{ background: "var(--ap-violet-soft)" }}>
                  {feature.example}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="ap-mesh ap-grid-overlay relative py-10">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">The Transformation</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-4">
              From auto-rejected<br />to <span className="ap-grad-text">shortlisted.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="ap-card border-2 border-red-200 p-8">
              <h3 className="ap-display text-2xl font-bold mb-6 text-red-500">Before PlacementOS</h3>
              <ul className="space-y-4">
                {[
                  "Applied to 200+ roles on Naukri — 0 callbacks",
                  "Resume had skill-bar graphics ATS couldn't parse",
                  "\"Created dashboards\" — no numbers, no impact",
                  "Stuck at ₹4.5 LPA at a service company",
                  "No idea which roles actually match skills",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-[var(--ap-ink-2)]">
                    <span className="text-red-500 text-xl">×</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="ap-card border-2 p-8" style={{ borderColor: "var(--ap-green)" }}>
              <h3 className="ap-display text-2xl font-bold mb-6 text-[var(--ap-green)]">After PlacementOS</h3>
              <ul className="space-y-4">
                {[
                  "Applied to 18 matched roles — got 6 callbacks",
                  "ATS score jumped from 24 → 91 after fixes",
                  "\"Built Tableau dashboard tracking 12 KPIs, reducing report time by 40%\"",
                  "Clear path: ₹4.5L → ₹14L in 10 months",
                  "Placed as Sr. Data Analyst at a fintech startup",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-[var(--ap-ink-2)]">
                    <CheckCircle className="w-6 h-6 text-[var(--ap-green)] flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-10">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">Real Results</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-4">
              Data analysts who stopped<br />refreshing Naukri and <span className="ap-grad-text">got placed.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, idx) => (
              <div key={idx} className="ap-card ap-pop p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, i) => (
                    <span key={i} className="text-[var(--ap-orange)]">★</span>
                  ))}
                </div>
                <p className="text-[var(--ap-ink-2)] mb-4 leading-relaxed">{t.text}</p>
                <div className="flex items-center gap-2 text-[var(--ap-green)] font-semibold mb-4">
                  <CheckCircle className="w-5 h-5" />
                  {t.result}
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative w-12 h-12 text-white rounded-full flex items-center justify-center font-bold ap-display overflow-hidden shrink-0" style={{ background: "var(--ap-fire)" }}>
                    {t.initials}
                    {t.img && <img src={t.img} alt={t.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={(e) => e.currentTarget.remove()} />}
                  </div>
                  <div>
                    <div className="ap-display font-semibold text-[var(--ap-ink)]">{t.name}</div>
                    <div className="text-xs text-[var(--ap-muted)]">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="bg-[var(--ap-canvas)] py-10">
        <div className="ap-section max-w-4xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">Launch Pricing</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-4">
              One analysis. Everything you need<br />to <span className="ap-grad-text">get placed.</span>
            </h2>
            <div className="mt-4">
              <CountdownTimer onExpired={() => setOfferExpired(true)} />
            </div>
          </div>
          <div className="ap-card p-8 md:p-12" style={{ borderColor: "var(--ap-violet)", borderWidth: 2 }}>
            <div className="text-center mb-8">
              <span className="ap-chip mb-4" style={{ color: "var(--ap-violet)", borderColor: "var(--ap-violet-soft)", background: "var(--ap-violet-soft)" }}>
                {offerExpired ? "Price Updated" : "Launch Offer — 90% Off"}
              </span>
              <h3 className="ap-display text-3xl font-bold text-[var(--ap-ink)] mb-2">Complete Career Analysis</h3>
              <p className="text-[var(--ap-muted)]">ATS diagnosis + career roadmap + smart job matching</p>
            </div>
            <div className="space-y-3 mb-8">
              {[
                { item: "ATS Score & Full Blocker Analysis", price: "₹499" },
                { item: "Copy-Paste Fix Recommendations", price: "₹399" },
                { item: "30/60/90-Day Career Roadmap", price: "₹599" },
                { item: "Smart Job Matching + Fit Scores", price: "₹299" },
                { item: "Career Velocity Simulation", price: "₹99" },
              ].map((row, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-[var(--ap-line)]">
                  <span className="flex items-center gap-2 text-[var(--ap-ink-2)]">
                    <CheckCircle className="w-5 h-5 text-[var(--ap-green)]" />
                    {row.item}
                  </span>
                  <span className="text-[var(--ap-muted)] line-through">{row.price}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center py-4 border-t-2 border-[var(--ap-line)] mb-8">
              <span className="text-lg font-semibold text-[var(--ap-ink)]">Total Value</span>
              <span className="text-lg font-semibold text-[var(--ap-ink)]">₹1,995</span>
            </div>
            <div className="text-center mb-8">
              <p className="text-[var(--ap-muted)] mb-2">You pay today</p>
              <div className="ap-display text-6xl font-bold text-[var(--ap-ink)]">
                {offerExpired ? (
                  <><span className="text-3xl">₹</span>499<p className="text-base text-[var(--ap-muted)] mt-2 font-normal">Offer expired</p></>
                ) : (
                  <><span className="text-3xl">₹</span>99<p className="text-[var(--ap-muted)] mt-2 text-base font-normal">One-time payment · Lifetime access</p></>
                )}
              </div>
            </div>
            <button
              onClick={startAnalysis}
              className="ap-btn ap-btn-fire ap-btn-lg w-full"
              data-testid="da-pricing-cta"
            >
              Start My Free Analysis
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="text-center text-[var(--ap-muted)] text-sm mt-4 flex items-center justify-center gap-2">
              <CheckCircle className="w-4 h-4 text-[var(--ap-green)]" />
              Free analysis first — pay only if you want the full report
            </p>
            <div className="flex justify-center gap-6 mt-6 text-sm text-[var(--ap-muted)]">
              <span className="flex items-center gap-1"><Lock className="w-4 h-4" /> Secure</span>
              <span className="flex items-center gap-1"><Shield className="w-4 h-4" /> SSL</span>
              <span className="flex items-center gap-1"><Zap className="w-4 h-4" /> Instant</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10">
        <div className="ap-section max-w-4xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">Questions</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)]">Frequently asked.</h2>
          </div>
          <div className="mt-12 space-y-5">
            {faqs.map((f, i) => {
              const open = faqOpen === i;
              return (
                <div key={i} className="ap-card overflow-hidden transition-shadow"
                  style={open ? { borderColor: "var(--ap-violet)", boxShadow: "0 18px 50px -24px rgba(91,61,245,.4)" } : {}}>
                  <button onClick={() => setFaqOpen(open ? -1 : i)} className="group w-full flex items-center justify-between gap-4 text-left px-6 py-6 bg-transparent border-0 cursor-pointer">
                    <span className="ap-display font-bold text-[16.5px] text-[var(--ap-ink)] group-hover:text-[var(--ap-violet)] transition-colors">{f.question}</span>
                    <span className="shrink-0 w-8 h-8 rounded-full grid place-items-center transition-colors"
                      style={{ background: open ? "var(--ap-violet)" : "var(--ap-violet-soft)", color: open ? "#fff" : "var(--ap-violet)" }}>
                      <Plus className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
                    </span>
                  </button>
                  <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden"><p className="px-6 pb-6 text-[15px] text-[var(--ap-muted)] leading-relaxed">{f.answer}</p></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="ap-mesh ap-grid-overlay relative py-10">
        <div className="ap-section max-w-4xl text-center">
          <p className="ap-eyebrow mb-4">Ready?</p>
          <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-6">
            Your next role is waiting.<br />
            Your resume just isn't <span className="ap-grad-text">reaching it.</span>
          </h2>
          <p className="text-xl text-[var(--ap-muted)] mb-8">
            In 2 minutes, you'll know exactly what's blocking your applications — and exactly how to fix it.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={startAnalysis} className="ap-btn ap-btn-fire ap-btn-lg">
              Start My Free Analysis <ArrowRight className="w-5 h-5" />
            </button>
            <a href="#features" className="ap-btn ap-btn-ghost ap-btn-lg">
              See All Features
            </a>
          </div>
          <div className="mt-6">
            <a href="/find-jobs" className="text-[var(--ap-muted)] text-sm hover:text-[var(--ap-ink-2)] underline underline-offset-2">Or find a free job →</a>
          </div>
        </div>
      </section>

      <StickyCTABar heroRef={heroRef} onCTAClick={startAnalysis} />
    </div>
  );
}
